import { useState } from 'react';
import { BordButton, BordFlag } from 'bord-design-system';
import { toCountryCode } from 'components/NudosComponents/BordFlag/BordFlag.types';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import { formatDateToPicker, getQuoteExpirationLocalTime, getTodayDateForPicker } from 'utils/formatDatesAndHours';
import './QuoteConfirmationModalBody.scss';

interface IQuoteConfirmationModalBody {
  quoteId: number;
  clientName: string;
  countryName: string;
  countryCode: string;
  quotedToolsCount: number;
  totalAmount: string;
  organizationId?: number;
  onClose: () => void;
  onConfirm: (expirationDate: string) => void;
}

/** Fila de resumen de la cotización con etiqueta y valor. */
interface ISummaryRow {
  label: string;
  value: string | JSX.Element;
}

/**
 * Cuerpo del modal de confirmación: resumen de la cotización, selector de fecha de vencimiento
 * y acciones de envío y cancelación.
 */
const QuoteConfirmationModalBody = ({
  quoteId,
  clientName,
  countryName,
  countryCode,
  quotedToolsCount,
  totalAmount,
  organizationId,
  onClose,
  onConfirm
}: IQuoteConfirmationModalBody) => {
  const [expirationDate, setExpirationDate] = useState('');
  const [dateError, setDateError] = useState('');

  const minExpirationDate = getTodayDateForPicker();
  const expirationLocalTime = getQuoteExpirationLocalTime();
  const isConfirmDisabled = !expirationDate || !!dateError;

  const goToOrganizationDetails = () => {
    if (!organizationId) return;
    window.open(`/organization/details/${organizationId}`, '_blank');
  };

  /**
   * Actualiza la fecha de vencimiento y valida que no sea anterior a hoy, mostrando un error visible si lo es.
   * @param newDate datetime ISO en utc-0 devuelto por el date picker
   */
  const handleDateChange = (newDate: string) => {
    setExpirationDate(newDate);
    const selectedDate = formatDateToPicker(newDate);
    const isPastDate = !!selectedDate && selectedDate < minExpirationDate;
    setDateError(isPastDate ? 'La fecha de vencimiento no puede ser anterior a hoy' : '');
  };

  /** Confirma el envío de la cotización si la fecha de vencimiento es válida. */
  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    onConfirm(expirationDate);
  };

  const summaryRows: ISummaryRow[] = [
    { label: 'N° de cotización', value: String(quoteId) },
    {
      label: 'Cliente',
      value: (
        <span className={`clientValue${organizationId ? ' clickable' : ''}`} onClick={goToOrganizationDetails}>
          {clientName}
          {!!organizationId && <BordOneToneIcon variant="externalLink" standardSize={14} stroke="#00B188" />}
        </span>
      )
    },
    {
      label: 'País',
      value: (
        <span className="countryValue">
          {countryName} <BordFlag country={toCountryCode(countryCode)} variant="circle" standardSize={12} />
        </span>
      )
    },
    { label: 'Herramientas cotizadas', value: String(quotedToolsCount) },
    { label: 'Monto total', value: totalAmount }
  ];

  return (
    <div className="quoteConfirmationModalBody">
      <div className="summarySection">
        {summaryRows.map(({ label, value }) => (
          <div key={label} className="summaryRow">
            <span className="summaryLabel">{label}</span>
            <span className="summaryValue">{value}</span>
          </div>
        ))}
      </div>
      <div className="approvalSection">
        <div className="approvalTitle">Tiempo para aprobación del cliente</div>
        <div className="approvalDescription">
          Si el cliente no aprueba en este plazo, la cotización se vencerá automáticamente y las herramientas no podrán
          ser compradas.
        </div>
        <NudosDatePicker
          label="Fecha de vencimiento*"
          isFilled={!!expirationDate}
          updateCurrentValueCallback={handleDateChange}
          minDate={minExpirationDate}
          errorText={dateError}
        />
        <div className="dateNote">La cotización se vencerá a las {expirationLocalTime} del día seleccionado</div>
      </div>
      <BordButton
        label="Enviar cotización"
        buttonIconOne="send"
        disabled={isConfirmDisabled}
        onClick={handleConfirm}
        customClassName="sendBtn"
      />
      <button className="cancelLink" onClick={onClose}>
        Cancelar
      </button>
    </div>
  );
};

export default QuoteConfirmationModalBody;
